import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Tabs,
  Tab,
  FormControl,
  Select,
  MenuItem,
  Divider,
  Modal,
} from "@mui/material";
import MainLayout from "../../components/MainLayout";
import CounterSection from "../../components/Auditions/CounterSection";
import AllAuditions from "./components/AllAuditions";
import { LocationOn } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { setSelectedLocation, setSelectedTab } from "../../actions/auditions";
import PastAuditions from "./components/PastAuditions";
import DraftAuditions from "./components/DraftAuditions";
import ProjectForm from "../../components/Auditions/ProjectForm";
import RoleForm from "../../components/Auditions/RoleForm";
import SlideStack from "../../components/SlideStack";
import { getLocationList, setLoading } from "../../actions/project";
import useMediaQuery from "../../../base/useMediaQuery";
import { locationDropdownOptions } from "../../../constants";
import ActiveAuditions from "./components/ActiveAuditions";
import bgImage from "../../../assets/images/card-image2.png";
import AutoFillAICard from "./components/AutoFillAiCard";
import { getArtistExperienceCategoryList } from "../../actions/general";
import clsx from "clsx";
import { setSelectedRecruiter } from "../../actions/recruiter";

/**
 * @author Sarthak Sharma.
 * @since 29-12-2023.
 * @description Auditionss management page.
 * @returns React.ReactElement
 */

const AuditionsManagement = () => {
  const dispatch = useDispatch();

  const min900px = useMediaQuery("(min-width: 900px)");
  const min530px = useMediaQuery("(min-width: 530px)");

  const isMobile = useMediaQuery("(max-width:600px)");
  const isTablet = useMediaQuery("(max-width:900px)");

  const allAudition = useRef(null);
  const activeAudition = useRef(null);
  const draftAudition = useRef(null);
  const pastAudition = useRef(null);

  const isOpenAi = useRef(false);

  const selectedAuditionTab =
    useSelector((state) => state.auditionReducer.selectedAuditionTab) ?? 0;
  const selectedLocation =
    useSelector((state) => state.auditionReducer.selectedLocation) ?? "ALL";

  const [modalOpen, setModalOpen] = useState(false);
  const [aiModalOpen, setAiModalOpen] = useState(false);

  const handleChange = (event, newValue) => {
    dispatch(setSelectedTab(newValue));
  };

  const handleChangeLocation = (event) => {
    dispatch(setSelectedLocation(event.target.value));
  };

  useEffect(() => {
    // dispatch(setSelectedTab(0));
    dispatch(getLocationList());
    dispatch(getArtistExperienceCategoryList({ categoryType: "Artist" }));
  }, []);

  const onCloseForm = () => {
    dispatch(setSelectedRecruiter(null));
    setModalOpen(false);
    dispatch(setLoading(false));
    isOpenAi.current = false;
    switch (selectedAuditionTab) {
      case 0:
        allAudition?.current?.refreshData();
        break;
      case 1:
        activeAudition?.current?.refreshData();
        break;
      case 2:
        pastAudition?.current?.refreshData();
        break;
      case 3:
        draftAudition?.current?.refreshData();
        break;
    }
  };

  const onAiSubmit = () => {
    isOpenAi.current = true;
    setModalOpen(true);
  };

  const onCreateProject = () => {
    isOpenAi.current = false;
    setModalOpen(true);
  };

  const handleClose = () => {
    isOpenAi.current = false;
    dispatch(setLoading(false));
    setModalOpen(false);
    dispatch(setSelectedRecruiter(null));
  };

  const handleRemoveAuditions = () => {
    if (selectedAuditionTab === 0) {
      allAudition.current?.delete();
    } else if (selectedAuditionTab === 1) {
      activeAudition.current?.delete();
    } else if (selectedAuditionTab === 2) {
      pastAudition.current?.delete();
    } else {
      draftAudition.current?.delete();
    }
  };

  const SideBarData = [
    {
      name: "ProjectForm",
      component: ProjectForm,
      props: { onClose: onCloseForm, open: modalOpen, isAi: isOpenAi.current },
    },
    {
      name: "RoleForm",
      component: RoleForm,
      props: { onClose: onCloseForm, open: modalOpen },
    },
  ];

  return (
    <MainLayout
      open={modalOpen}
      onClose={handleClose}
      FormComponent={
        <SlideStack open={modalOpen} components={SideBarData} showRecruiter />
      }
    >
      <Box
        className="relative w-full flex justify-between items-center px-4 pt-7"
        sx={{
          flexDirection: min530px ? "row" : "column",
          // alignItems: "flex-start",
          gap: min530px ? "0" : "12px",
        }}
      >
        <h2
          className="font-['Poppins-Medium'] leading-[30px] text-[24px]"
          style={{
            textAlign: min530px ? "left" : "left",
            width: min530px ? "auto" : "100%",
          }}
        >
          Auditions
        </h2>

        <Box
          className={clsx("flex items-center gap-3", {
            "w-full justify-end": min530px,
            "w-full justify-center": !min530px,
          })}
          style={{
            marginTop: min530px ? "0" : "10px",
            flexWrap: "nowrap",
          }}
        >
          <Button
            disabled={modalOpen}
            onClick={setAiModalOpen?.bind(this, true)}
            variant="contained"
            className={clsx(
              "font-['Poppins-Medium'] normal-case text-white flex items-center",
              {
                "bg-[#0D99FF]": !modalOpen,
              }
            )}
            sx={{
              minWidth: min530px ? "120px" : "100px",
              fontSize: min530px ? "14px" : "12px",
              padding: min530px ? "6px 12px" : "5px 8px",
              whiteSpace: "nowrap", //
            }}
          >
            Create with AI
          </Button>

          <Button
            disabled={modalOpen}
            onClick={onCreateProject}
            variant="contained"
            className="font-['Poppins-Medium'] normal-case text-white"
            sx={{
              minWidth: min530px ? "120px" : "100px",
              fontSize: min530px ? "14px" : "12px",
              padding: min530px ? "6px 12px" : "5px 8px",
              whiteSpace: "nowrap",
            }}
          >
            Create
          </Button>

          <Button
            variant="contained"
            onClick={handleRemoveAuditions}
            className="font-['Poppins-Medium'] bg-[#800000] normal-case text-white"
            sx={{
              minWidth: min530px ? "120px" : "100px",
              fontSize: min530px ? "14px" : "12px",
              padding: min530px ? "6px 12px" : "5px 8px",
              whiteSpace: "nowrap",
            }}
          >
            Remove
          </Button>
        </Box>
      </Box>

      <Box className="relative w-full flex flex-col gap-8 items-center p-4 py-4">
        <CounterSection />
      </Box>
      <Box className="relative w-full flex flex-col gap-8 items-center p-4 py-4">
        <Box className="w-full max-w-[1567px] flex flex-col bg-[#ffffff] rounded-[12px] border-[2px] border-[#E8E8E9] pb-4">
          <Box
            className={`flex justify-between items-stretch ${
              isMobile ? "flex-col" : ""
            }`}
            sx={{
              borderBottom: 1,
              borderColor: "divider",
              width: "100%",
              paddingX: 3,
            }}
          >
            <Tabs
              variant={min900px ? "standard" : "scrollable"}
              scrollButtons
              allowScrollButtonsMobile
              className="w-full pt-4"
              TabIndicatorProps={{ style: { height: 4 } }}
              value={selectedAuditionTab}
              onChange={handleChange}
            >
              <Tab
                className="font-['Poppins-Regular'] normal-case text-[14px]"
                label="All"
              />
              <Tab
                className="font-['Poppins-Regular'] normal-case text-[14px]"
                label="Active"
              />
              <Tab
                className="font-['Poppins-Regular'] normal-case text-[14px]"
                label="Past Auditions"
              />
              <Tab
                className="font-['Poppins-Regular'] normal-case text-[14px]"
                label="Draft"
              />
              {/* <Tab className="font-['Poppins-Regular'] normal-case text-[14px]" label="Closed" /> */}
            </Tabs>
            <div className={`mx-4 ${isMobile ? "hidden" : ""}`}>
              <Divider orientation="vertical" />
            </div>
            <Box
              className={`flex gap-2 ${
                isMobile ? "items-end ml-auto py-5" : "items-center"
              }`}
            >
              <div className="flex flex-col items-center">
                <LocationOn className="text-[20px]" />
                <div className="w-[7px] border-b-[1px] border-[#5C5F62]" />
              </div>
              <FormControl
                className="no-border"
                variant="standard"
                size="small"
              >
                <Select
                  label=""
                  value={selectedLocation}
                  onChange={handleChangeLocation}
                  sx={{ fontFamily: "Poppins-Regular", fontSize: "14px" }}
                >
                  <MenuItem
                    sx={{ fontFamily: "Poppins-Regular", fontSize: "14px" }}
                    value={"ALL"}
                  >
                    All Locations
                  </MenuItem>
                  {locationDropdownOptions?.map((item) => (
                    <MenuItem
                      sx={{ fontFamily: "Poppins-Regular", fontSize: "14px" }}
                      key={item.value}
                      value={item.value}
                    >
                      {item.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>
          <Modal open={aiModalOpen} onClose={setAiModalOpen?.bind(this, false)}>
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                width: isMobile ? "90vw" : isTablet ? "80vw" : 850,
                height: isMobile ? "80vh" : isTablet ? "70vh" : 600,
                boxShadow: 24,
                p: isMobile ? 1.5 : isTablet ? 2 : 3,
                textAlign: "center",
                borderRadius: "8px",
                backgroundColor: "#fff",
                backgroundImage: `url(${bgImage})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                backgroundRepeat: "no-repeat",
                overflow: "hidden", // Prevents scrolling issues
              }}
            >
              <AutoFillAICard
                setAiModalOpen={setAiModalOpen}
                onSubmit={onAiSubmit}
              />
            </Box>
          </Modal>
          <AllAuditions ref={allAudition} visible={selectedAuditionTab === 0} />
          <ActiveAuditions
            ref={activeAudition}
            visible={selectedAuditionTab === 1}
          />
          <PastAuditions
            ref={pastAudition}
            visible={selectedAuditionTab === 2}
          />
          <DraftAuditions
            ref={draftAudition}
            visible={selectedAuditionTab === 3}
          />
        </Box>
      </Box>
    </MainLayout>
  );
};

export default React.memo(AuditionsManagement, () => true);
